import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  grossCodesService,
  CreateGrossCodeInput,
  UpdateGrossCodeInput,
} from "../../services/admin/grosscodes.service";

export function useGrossCodesAdmin() {
  return useQuery({
    queryKey: ["admin", "gross-codes"],
    queryFn: grossCodesService.getAll,
  });
}

export function useGrossCode(id?: string) {
  return useQuery({
    queryKey: ["admin", "gross-codes", id],
    queryFn: () => grossCodesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateGrossCodeInput) =>
      grossCodesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "gross-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
      notifications.show({
        title: "Success",
        message: "Accessioning Gross Code created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create accessioning gross code",
        color: "red",
      });
    },
  });
}

export function useUpdateGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateGrossCodeInput) =>
      grossCodesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "gross-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
      notifications.show({
        title: "Success",
        message: "Accessioning Gross Code updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update accessioning gross code",
        color: "red",
      });
    },
  });
}

export function useToggleGrossCodeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      grossCodesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "gross-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
      notifications.show({
        title: "Success",
        message: "Accessioning Gross Code status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message:
          error.message || "Failed to update accessioning gross code status",
        color: "red",
      });
    },
  });
}

export function useDeleteGrossCode() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => grossCodesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "gross-codes"] });
      queryClient.invalidateQueries({ queryKey: ["gross-codes"] });
      notifications.show({
        title: "Success",
        message: "Accessioning Gross Code deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete accessioning gross code",
        color: "red",
      });
    },
  });
}
